import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import SearchableSelect from '../SearchableSelect'
import { searchCustomers } from '../../lib/customerSearch'
import { useAuth } from '../../contexts/AuthContext'
import { exportGroupExcel } from '../../lib/groupExport'
import {
  fetchGroupRows, fetchGroupTotals, addGroupRow, updateGroupRow, setGroupRowCancelled, searchAirports,
  type GroupRow, type GroupTotals, type GroupRowPatch, type PickOption,
} from '../../lib/groupRows'

// Job Group -> Dashboard: one row per real child job, only the 12 fields, edited in place. Every edit calls the database
// (group_update_row = editing that job) and then re-reads rows AND totals, so a subtotal is never a number this page computed or cached.
// Subtotals / month total come straight from group_destination_totals. Rows without a destination are listed in their own red block and
// are never merged into another group or dropped.

const fmt = (v: number, dp = 3) => v.toLocaleString('en-GB', { maximumFractionDigits: dp })
const inputCls = 'w-full border border-blue-400 rounded px-1 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white dark:bg-gray-700 dark:text-white'

// Click-to-edit text / number cell (same convention as the note editor: shows plain text, click swaps in an input, Enter/blur saves, Esc cancels).
function EditCell({ value, display, onCommit, numeric = false, disabled = false, title }: {
  value: string; display?: string; onCommit: (v: string) => void; numeric?: boolean; disabled?: boolean; title?: string
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value)
  const cancelled = useRef(false)
  if (!editing) {
    return (
      <span title={title} onClick={() => { if (!disabled) { cancelled.current = false; setDraft(value); setEditing(true) } }}
        className={`block min-h-[1.25rem] rounded px-1 ${disabled ? 'text-gray-500' : 'cursor-text hover:bg-yellow-50 dark:hover:bg-yellow-900/20'} ${numeric ? 'text-right' : ''}`}>
        {(display ?? value) || <span className="text-gray-300">—</span>}
      </span>
    )
  }
  const finish = () => { setEditing(false); if (!cancelled.current && draft !== value) onCommit(draft) }
  return (
    <input autoFocus className={`${inputCls} ${numeric ? 'text-right' : ''}`} value={draft} inputMode={numeric ? 'decimal' : undefined}
      onChange={e => setDraft(e.target.value)} onBlur={finish}
      onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); if (e.key === 'Escape') { cancelled.current = true; e.currentTarget.blur() } }} />
  )
}

const customerOptions = async (term: string): Promise<PickOption[]> =>
  (await searchCustomers({ term })).map(c => ({ id: c.id, code: c.code, name: c.name, label: c.name }))

function PartyCell({ id, name, onPick, disabled }: { id: string | null; name: string | null; onPick: (id: string | null) => void; disabled: boolean }) {
  if (disabled) return <span className="px-1">{name || '—'}</span>
  return (
    <SearchableSelect options={[]} value={id || ''} onChange={v => onPick(v || null)} onSearch={customerOptions} allowCreate={false} usePortal
      selectedOption={id ? { id, label: name || '…' } : null} placeholder="Search…" />
  )
}

function PortCell({ id, code, name, onPick, disabled, invalid }: { id: string | null; code: string | null; name: string | null; onPick: (id: string | null) => void; disabled: boolean; invalid?: boolean }) {
  if (disabled) return <span className="px-1">{code || '—'}</span>
  return (
    <SearchableSelect options={[]} value={id || ''} onChange={v => onPick(v || null)} onSearch={searchAirports} allowCreate={false} usePortal invalid={invalid}
      selectedOption={id ? { id, label: `${code ?? ''} - ${name ?? ''}` } : null} placeholder="Airport…" />
  )
}

const COLS = ['Row', 'Airway Bill', 'Flight No.', 'Shipper', 'Consignee', 'Description', 'From', 'To', 'Cartons', 'PMC', 'Gross Wt (kg)', 'Chargeable Wt (kg)', 'CBM', '']

export default function GroupDashboard({ groupId, groupRef, customerName, editable, highlightJobId, onOpenNotes }: {
  groupId: string; groupRef: string; customerName: string | null; editable: boolean; highlightJobId?: string | null; onOpenNotes?: () => void
}) {
  const { userEmail } = useAuth()
  const [exporting, setExporting] = useState(false)
  const [rows, setRows] = useState<GroupRow[]>([])
  const [totals, setTotals] = useState<GroupTotals[]>([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showRemoved, setShowRemoved] = useState(false)
  const highlightRef = useRef<HTMLTableRowElement | null>(null)

  const reload = useCallback(async () => {
    try {
      const [r, t] = await Promise.all([fetchGroupRows(groupId), fetchGroupTotals(groupId)])
      setRows(r); setTotals(t); setError(null)
    } catch (e) { setError(e instanceof Error ? e.message : 'Could not load the group') }
    finally { setLoading(false) }
  }, [groupId])
  useEffect(() => { reload() }, [reload])
  useEffect(() => { if (!loading && highlightJobId) highlightRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' }) }, [loading, highlightJobId])

  // One place that runs a change and re-reads everything.
  async function run(fn: () => Promise<unknown>) {
    setBusy(true); setError(null)
    try { await fn() } catch (e) { setError(e instanceof Error ? e.message : 'Change failed') }
    await reload()
    setBusy(false)
  }
  const edit = (jobId: string, patch: GroupRowPatch) => run(() => updateGroupRow(jobId, patch))
  const num = (s: string, integer = false): number | null | 'bad' => {
    const t = s.trim(); if (t === '') return null
    const v = Number(t)
    if (!Number.isFinite(v) || v < 0 || (integer && !Number.isInteger(v))) return 'bad'
    return v
  }
  const editNum = (jobId: string, key: 'cartons' | 'gross_weight' | 'chargeable_weight' | 'cbm' | 'pmc_count', s: string, integer = false) => {
    const v = num(s, integer)
    if (v === 'bad') { setError(`${key.replace('_', ' ')}: enter ${integer ? 'a whole number' : 'a number'} of 0 or more`); return }
    return edit(jobId, { [key]: v } as GroupRowPatch)
  }

  const live = rows.filter(r => !r.cancelled)
  const removed = rows.filter(r => r.cancelled)
  const unassigned = live.filter(r => !r.to_port_id)
  const destTotals = totals.filter(t => t.sort_key === 1)
  const unassignedTotal = totals.find(t => t.sort_key === 2)
  const grand = totals.find(t => t.sort_key === 3)
  const last = live[live.length - 1] ?? null
  const hasMulti = (r: GroupRow) => r.goods_lines > 1

  function rowCells(r: GroupRow, dim = false) {
    const ro = !editable || r.cancelled
    const multi = hasMulti(r)
    const roGoods = ro || multi
    const goodsTitle = multi ? `This job has ${r.goods_lines} goods lines: the figures are sums and are edited on the job's own page` : undefined
    return (
      <tr key={r.job_id} ref={r.job_id === highlightJobId ? highlightRef : undefined}
        className={`${dim ? 'opacity-50' : ''} ${r.job_id === highlightJobId ? 'bg-yellow-50 dark:bg-yellow-900/20 ring-2 ring-yellow-400' : ''} border-t border-gray-100 dark:border-gray-700 align-top`}>
        <td className="px-2 py-1.5 whitespace-nowrap font-mono text-xs">
          {r.job_ref}
          {r.notes_count > 0 && <button onClick={onOpenNotes} title="Collection/Delivery Notes raised for this row" className="ml-1.5 px-1.5 py-0.5 rounded-full bg-teal-100 text-teal-700 text-[10px] dark:bg-teal-900/30 dark:text-teal-300">Noted ({r.notes_count})</button>}
        </td>
        <td className="px-1 py-1 min-w-[8rem]"><EditCell disabled={ro} value={r.awb ?? ''} onCommit={v => edit(r.job_id, { awb: v })} /></td>
        <td className="px-1 py-1 min-w-[5rem]"><EditCell disabled={ro} value={r.flight_no ?? ''} onCommit={v => edit(r.job_id, { flight_no: v })} /></td>
        <td className="px-1 py-1 min-w-[10rem]"><PartyCell disabled={ro} id={r.shipper_id} name={r.shipper_name} onPick={id => edit(r.job_id, { shipper_id: id })} /></td>
        <td className="px-1 py-1 min-w-[10rem]"><PartyCell disabled={ro} id={r.consignee_id} name={r.consignee_name} onPick={id => edit(r.job_id, { consignee_id: id })} /></td>
        <td className="px-1 py-1 min-w-[9rem]"><EditCell disabled={roGoods} title={goodsTitle} value={r.description ?? ''} onCommit={v => edit(r.job_id, { description: v })} /></td>
        <td className="px-1 py-1 min-w-[7rem]"><PortCell disabled={ro} id={r.from_port_id} code={r.from_code} name={r.from_name} onPick={id => edit(r.job_id, { from_port_id: id })} /></td>
        <td className="px-1 py-1 min-w-[7rem]"><PortCell disabled={ro} invalid={!r.to_port_id && !r.cancelled} id={r.to_port_id} code={r.to_code} name={r.to_name} onPick={id => edit(r.job_id, { to_port_id: id })} /></td>
        <td className="px-1 py-1 w-20"><EditCell numeric disabled={roGoods} title={goodsTitle} value={r.cartons ? String(r.cartons) : ''} onCommit={v => editNum(r.job_id, 'cartons', v, true)} /></td>
        <td className="px-1 py-1 w-14"><EditCell numeric disabled={ro} value={r.pmc_count ? String(r.pmc_count) : ''} onCommit={v => editNum(r.job_id, 'pmc_count', v, true)} /></td>
        <td className="px-1 py-1 w-24"><EditCell numeric disabled={roGoods} title={goodsTitle} value={r.gross_weight ? String(r.gross_weight) : ''} display={r.gross_weight ? fmt(r.gross_weight) : ''} onCommit={v => editNum(r.job_id, 'gross_weight', v)} /></td>
        <td className="px-1 py-1 w-24"><EditCell numeric disabled={roGoods} title="Typed by hand: this is the weight finance bills on" value={r.chargeable_weight ? String(r.chargeable_weight) : ''} display={r.chargeable_weight ? fmt(r.chargeable_weight) : ''} onCommit={v => editNum(r.job_id, 'chargeable_weight', v)} /></td>
        <td className="px-1 py-1 w-20"><EditCell numeric disabled={roGoods} title={goodsTitle} value={r.cbm ? String(r.cbm) : ''} display={r.cbm ? fmt(r.cbm, 4) : ''} onCommit={v => editNum(r.job_id, 'cbm', v)} /></td>
        <td className="px-2 py-1.5 whitespace-nowrap text-xs">
          {editable && !r.cancelled && (<>
            <button disabled={busy} onClick={() => run(() => addGroupRow(groupId, r.job_id))} title="New row with this row's shipper, consignee, description, From, To and flight (airway bill, cartons, weights, CBM and PMC left empty)" className="text-blue-600 hover:underline mr-2 dark:text-blue-400">Copy</button>
            <button disabled={busy} onClick={() => { if (window.confirm(`Remove ${r.job_ref} from this group? It leaves the totals; its number is never reused.`)) run(() => setGroupRowCancelled(r.job_id, true)) }} className="text-red-500 hover:underline">Remove</button>
          </>)}
          {editable && r.cancelled && <button disabled={busy} onClick={() => run(() => setGroupRowCancelled(r.job_id, false))} className="text-blue-600 hover:underline dark:text-blue-400">Restore</button>}
          {multi && <Link to={`/jobs/${r.job_id}?full=1`} className="ml-2 text-gray-500 hover:underline" title={goodsTitle}>Full job</Link>}
        </td>
      </tr>
    )
  }

  const totalRow = (t: GroupTotals, label: string, cls: string) => (
    <tr key={`${t.sort_key}-${t.dest_port_id}`} className={`${cls} font-semibold text-xs`}>
      <td className="px-2 py-1.5" colSpan={8}>{label} · {t.shipments} {t.shipments === 1 ? 'shipment' : 'shipments'}</td>
      <td className="px-2 py-1.5 text-right">{fmt(t.cartons, 0)}</td>
      <td className="px-2 py-1.5 text-right">{fmt(t.pmc_count, 0)}</td>
      <td className="px-2 py-1.5 text-right">{fmt(t.gross_weight)}</td>
      <td className="px-2 py-1.5 text-right">{fmt(t.chargeable_weight)}</td>
      <td className="px-2 py-1.5 text-right">{fmt(t.cbm, 4)}</td>
      <td />
    </tr>
  )

  if (loading) return <p className="text-sm text-gray-400 py-8 text-center">Loading…</p>

  const head = (
    <thead className="text-[11px] uppercase text-gray-500 dark:text-gray-400 text-left bg-gray-50 dark:bg-gray-800">
      <tr>{COLS.map((c, i) => <th key={i} className={`px-2 py-2 font-semibold whitespace-nowrap ${i >= 8 && i <= 12 ? 'text-right' : ''}`}>{c}</th>)}</tr>
    </thead>
  )

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-gray-600 dark:text-gray-300">
          {live.length} {live.length === 1 ? 'shipment' : 'shipments'}{removed.length > 0 && ` · ${removed.length} removed`}
          <span className="text-gray-400"> · subtotals are read from the database after every change</span>
        </p>
        <div className="flex items-center gap-3">
          <button disabled={exporting || busy} onClick={async () => {
            setExporting(true); setError(null)
            try { await exportGroupExcel({ id: groupId, ref: groupRef, customerName }, userEmail) }
            catch (e) { setError(e instanceof Error ? e.message : 'Export failed') }
            setExporting(false)
          }} title="Excel for finance: the same subtotals as this page, read from the database at the moment you click"
            className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50">
            {exporting ? 'Exporting…' : 'Export Excel'}
          </button>
          {removed.length > 0 && (
            <label className="text-xs text-gray-500 flex items-center gap-1"><input type="checkbox" checked={showRemoved} onChange={e => setShowRemoved(e.target.checked)} /> Show removed</label>
          )}
          {editable && (
            <button disabled={busy} onClick={() => run(() => addGroupRow(groupId, last?.job_id ?? null))}
              className="px-3 py-1.5 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">
              {last ? `+ New row (copy of ${last.job_ref})` : '+ New row'}
            </button>
          )}
        </div>
      </div>

      {error && <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 rounded-lg text-sm">{error}</div>}

      {live.length === 0 && <p className="text-sm text-gray-400 py-6 text-center">No shipments in this group yet. Add the first row.</p>}

      {unassigned.length > 0 && (
        <div className="border-2 border-red-400 rounded-lg overflow-hidden">
          <div className="px-3 py-2 bg-red-50 dark:bg-red-900/20 text-sm text-red-800 dark:text-red-300 font-medium">
            {unassigned.length} {unassigned.length === 1 ? 'row has' : 'rows have'} no destination (To). Fill it in: until then {unassigned.length === 1 ? 'it is' : 'they are'} counted only in this block and in the month total, not in any destination.
          </div>
          <div className="overflow-x-auto"><table className="min-w-full text-xs">{head}<tbody>
            {unassigned.map(r => rowCells(r))}
            {unassignedTotal && totalRow(unassignedTotal, 'No destination', 'bg-red-50 dark:bg-red-900/20')}
          </tbody></table></div>
        </div>
      )}

      {destTotals.map(t => {
        const rs = live.filter(r => r.to_port_id === t.dest_port_id)
        return (
          <div key={t.dest_port_id} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
            <div className="px-3 py-2 bg-blue-50 dark:bg-blue-900/20 text-sm font-semibold text-blue-900 dark:text-blue-200">To: {t.dest_code} — {t.dest_name}</div>
            <div className="overflow-x-auto"><table className="min-w-full text-xs">{head}<tbody>
              {rs.map(r => rowCells(r))}
              {totalRow(t, `Subtotal ${t.dest_code}`, 'bg-gray-100 dark:bg-gray-700')}
            </tbody></table></div>
          </div>
        )
      })}

      {grand && (
        <div className="border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
          <table className="min-w-full text-xs"><tbody>{totalRow(grand, 'MONTH TOTAL (for checking: equals the sum of the destinations above plus any rows without a destination)', 'bg-gray-200 dark:bg-gray-600')}</tbody></table>
        </div>
      )}

      {showRemoved && removed.length > 0 && (
        <div className="border border-dashed border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
          <div className="px-3 py-2 text-sm text-gray-500">Removed rows (not in any total)</div>
          <div className="overflow-x-auto"><table className="min-w-full text-xs">{head}<tbody>{removed.map(r => rowCells(r, true))}</tbody></table></div>
        </div>
      )}
    </div>
  )
}
